import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, DollarSign, FileText, TrendingUp, 
  Calendar, Clock, AlertCircle, CheckCircle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { analyticsService, agentsService, commissionsService, agencyService } from '../../services';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    pendingApplications: 0,
    totalClients: 0,
    activeQuotes: 0,
    monthlyRevenue: 0,
    pendingCommissions: 0,
    approvedCommissions: 0,
    revenueGrowth: 0,
    agentGrowth: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [topAgents, setTopAgents] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use new agency endpoints for agency-wide data
      const dashboardData = await agencyService.getDashboard();
      const { agents: agentsList } = await agencyService.getAgents({ limit: 10 });
      const { applications } = await agencyService.getApplications({ status: 'pending', limit: 5 });
      const { commissions: recentCommissions, stats: commissionStats } = await agencyService.getCommissions({ limit: 5 });
      const analyticsData = await agencyService.getAnalytics({ period: 'month' });

      // Set stats from agency dashboard data
      setStats({
        totalAgents: dashboardData.metrics.agents.total,
        activeAgents: dashboardData.metrics.agents.active,
        pendingApplications: dashboardData.metrics.agents.pending_applications,
        totalClients: dashboardData.metrics.quotes.monthly_count, // Using quotes as proxy for clients
        activeQuotes: dashboardData.metrics.quotes.monthly_count,
        monthlyRevenue: dashboardData.metrics.revenue.monthly,
        pendingCommissions: dashboardData.metrics.commissions.pending,
        approvedCommissions: dashboardData.metrics.commissions.approved,
        revenueGrowth: 12.5, // TODO: Calculate from historical data
        agentGrowth: 8.3 // TODO: Calculate from historical data
      });

      // Format recent activity
      const recentApplications = applications.slice(0, 3).map(app => ({
        id: app.id,
        type: 'application',
        message: `New agent application from ${app.first_name} ${app.last_name}`,
        time: new Date(app.submitted_at).toLocaleString(),
        status: 'pending'
      }));

      const formattedCommissions = recentCommissions.slice(0, 3).map(comm => ({
        id: comm.id,
        type: 'commission',
        message: `Commission submitted by ${comm.agent_name}`,
        time: new Date(comm.created_at).toLocaleString(),
        status: comm.status
      }));

      setRecentActivity([...recentApplications, ...formattedCommissions]
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, 5)
      );

      // Get top agents from analytics
      const topAgentsData = analyticsData.revenue_by_agent
        .slice(0, 3)
        .map((agent, index) => ({
          id: agent.id,
          name: agent.agent_name,
          revenue: agent.total_revenue,
          bookings: agent.commission_count
        }));
      
      setTopAgents(topAgentsData);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Agents',
      value: stats.totalAgents,
      subtitle: `${stats.activeAgents} active`,
      icon: Users,
      color: 'blue',
      trend: stats.agentGrowth,
      onClick: () => navigate('/admin/agents')
    },
    {
      title: 'Pending Applications',
      value: stats.pendingApplications,
      subtitle: 'Awaiting review',
      icon: AlertCircle,
      color: 'yellow',
      onClick: () => navigate('/admin/agents?tab=applications')
    },
    {
      title: 'Monthly Revenue',
      value: `$${stats.monthlyRevenue.toLocaleString()}`,
      subtitle: `${stats.revenueGrowth > 0 ? '+' : ''}${stats.revenueGrowth}% from last month`,
      icon: DollarSign,
      color: 'green',
      trend: stats.revenueGrowth
    },
    {
      title: 'Pending Commissions',
      value: stats.pendingCommissions,
      subtitle: `${stats.approvedCommissions} approved`,
      icon: Clock,
      color: 'orange',
      onClick: () => navigate('/admin/commissions')
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Agency Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back! Here's your agency overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            onClick={stat.onClick}
            className={`stats-card bg-white rounded-lg shadow p-6 ${
              stat.onClick ? 'cursor-pointer hover:shadow-lg' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.subtitle}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
            {stat.trend !== undefined && (
              <div className="flex items-center mt-4">
                {stat.trend > 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${
                  stat.trend > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(stat.trend)}%
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="p-6">
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${
                      activity.type === 'application' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      {activity.type === 'application' ? (
                        <Users className="w-4 h-4 text-blue-600" />
                      ) : (
                        <DollarSign className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      activity.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Agents */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Top Performing Agents</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topAgents.map((agent, index) => (
                <div key={agent.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                      <p className="text-xs text-gray-500">{agent.bookings} bookings</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    ${agent.revenue.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/admin/agents?tab=applications')}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <Users className="w-5 h-5 text-blue-600 mb-2" />
            <p className="font-medium text-gray-900">Review Applications</p>
            <p className="text-sm text-gray-500">
              {stats.pendingApplications} pending
            </p>
          </button>
          <button
            onClick={() => navigate('/admin/commissions')}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <DollarSign className="w-5 h-5 text-green-600 mb-2" />
            <p className="font-medium text-gray-900">Process Commissions</p>
            <p className="text-sm text-gray-500">
              {stats.pendingCommissions} pending
            </p>
          </button>
          <button
            onClick={() => navigate('/admin/tax-forms')}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <FileText className="w-5 h-5 text-purple-600 mb-2" />
            <p className="font-medium text-gray-900">Tax Forms</p>
            <p className="text-sm text-gray-500">Generate 1099s</p>
          </button>
          <button
            onClick={() => navigate('/admin/analytics')}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <TrendingUp className="w-5 h-5 text-orange-600 mb-2" />
            <p className="font-medium text-gray-900">View Analytics</p>
            <p className="text-sm text-gray-500">Performance reports</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
